import { Prettify } from '@xemplo/types';

type BaseProps = Record<string, unknown>;
type PrettyPick<T, K extends keyof T> = Prettify<Pick<T, K>>;
type PrettyRest<T, K extends keyof T> = Prettify<Omit<T, K>>;
type SplitResult<TBase extends BaseProps, TKeys extends readonly (keyof TBase)[]> = [
  PrettyPick<TBase, TKeys[number]>,
  PrettyRest<TBase, TKeys[number]>,
];

/**
 * Splits the given props into two objects:
 * one containing only the specified keys, and the other containing the rest.
 * This function ensures type safety and proper typing of the resulting objects.
 *
 *
 * @param props
 * @param pickKeys
 * @returns A tuple where the first element contains the picked props
 * and the second element contains the rest of the props.
 */
export function splitProps<
  TBase extends BaseProps, // The type of the props you wish to split
  TKeys extends readonly (keyof TBase)[], // The keys you want to pick from the base props
>(props: TBase | undefined, pickKeys: TKeys): SplitResult<TBase, TKeys> {
  const picked = {} as PrettyPick<TBase, TKeys[number]>;
  const rest = { ...(props ?? ({} as TBase)) };

  if (!props) return [picked, rest as PrettyRest<TBase, TKeys[number]>];

  for (const key of pickKeys) {
    if (Object.prototype.hasOwnProperty.call(props, key)) {
      picked[key] = props[key];
      delete rest[key];
    }
  }

  return [picked, rest as PrettyRest<TBase, TKeys[number]>];
}
